import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, Inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WINDOW } from '@core/injectors';
import { Ticket } from '@core/models';
import { ConfigService, UserService } from '@core/services';
import { AccountService } from '@features/server-side-authentication';
import { BehaviorSubject, catchError, EMPTY, finalize, tap, throwError } from 'rxjs';

@Component({
  selector: 'app-zendesk-tickets-table',
  templateUrl: './zendesk-tickets-table.component.html',
  styleUrls: ['./zendesk-tickets-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ZendeskTicketsTableComponent implements OnInit {
  selectedOrganization = input<number | null>(null);
  emptyListMessage = signal<string>('No data');
  isLoading = signal<boolean>(true);
  displayedColumns: string[] = ['number', 'title', 'status', 'lastUploaded', 'actions'];
  dataSource = new BehaviorSubject<Ticket[]>([]); // Initialize
  zendeskAgent: boolean | undefined; // To store the value

  private winRef!: WindowProxy | null;

  constructor(
    @Inject(WINDOW) private readonly win: Window,
    private readonly userService: UserService,
    private readonly accountService: AccountService,
    private readonly destroyRef: DestroyRef,
    private readonly configService: ConfigService,
  ) {
    this.userService
      .getUserData()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((userData) => {
        this.zendeskAgent = userData?.zendesk_agent;
      });
  }

  ngOnInit() {
    this.getUserTickets();
  }

  getUserTickets() {
    this.userService
      .getTickets(this.accountService.isInternal ? this.selectedOrganization() : null)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        tap((tickets) => {
          this.emptyListMessage.set('No issues found');
          // limit amount of records to 7
          if (tickets.length) {
            tickets = tickets.slice(0, 7);
          }
          this.dataSource.next(tickets);
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status >= 400 && error.status < 500) {
            this.emptyListMessage.set('Unable to display data. Please try again later.');
            this.dataSource.next([]);
            return EMPTY; // Stop the observable chain
          } else {
            // Other error types
            console.error('An error occurred:', error);
            return throwError(() => error); // Re-throw for potential error handling elsewhere
          }
        }),
      )
      .subscribe();
  }

  onClickOpenZendeskTicket(event: Event, key: string) {
    event.preventDefault();
    event.stopPropagation();

    const url = this.zendeskAgent
      ? `${this.configService.zendeskUrl}/agent/tickets/${key}`
      : `${this.configService.zendeskUrl}/hc/en-us/requests/${key}`;

    this.openTab(key, url);
  }

  seeMore() {
    const url = this.configService.zendeskUrl + (this.zendeskAgent ? '/agent/' : '/hc/en-us/requests/organization');
    this.openTab('all', url);
  }

  private openTab(key: string, url: string) {
    if (!this.winRef || this.winRef.closed) {
      this.winRef = this.win.open(url, '_blank');
      this.winRef?.focus();
    } else {
      this.winRef.location.href = url;
      this.winRef.focus();
    }
  }
}
